import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, TrendingUp, Brain, BarChart3, AlertCircle, Trash2, Eye, Target, Settings, Shield, GitCompare, Activity } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { supabase } from '@/lib/supabase';
import { TradingPatternAnalysisService, type TradingFile, type TradingPattern, type CSVTradeData } from '@/services/TradingPatternAnalysisService';
import { TradingAnalysisOrchestrator } from '@/services/trading-analysis/TradingAnalysisOrchestrator';
import { AnalyzerFactory } from '@/services/trading-analysis/AnalyzerFactory';
import { AnalysisMode, AnalysisConfig, DEFAULT_CONFIGS, AnalysisResult, PredictionResult } from '@/types/trading-analysis-types';
import { AnalysisResultsCard } from '@/components/admin/trading-analysis/AnalysisResultsCard';
import { EnhancedPredictionResults } from '@/components/admin/trading-analysis/EnhancedPredictionResults';
import { ComprehensiveAnalysisResults } from '@/components/admin/trading-analysis/ComprehensiveAnalysisResults';
import { ProgressiveBacktestTab } from '@/components/admin/trading-analysis/ProgressiveBacktestTab';
import { OptimizationTab } from '@/components/admin/trading-analysis/optimization/OptimizationTab';
import { RealTimePredictionsTab } from '@/components/admin/trading-analysis/RealTimePredictionsTab';
// Removed PatternRefreshManager and PatternBackupsManager imports (backed up as .OLD files)
import { HonestProgressiveBacktestTab } from '@/components/admin/trading-analysis/HonestProgressiveBacktestTab';
import { ManualPredictionComparison } from '@/components/admin/trading-analysis/ManualPredictionComparison';
import { CrossPatternComparisonTab } from '@/components/admin/trading-analysis/CrossPatternComparisonTab';
import { SequenceComparisonTab } from '@/components/admin/trading-analysis/SequenceComparisonTab';
import { TradeValueEditor } from '@/components/admin/trading-analysis/TradeValueEditor';
import { DuplicateTimestampChecker } from '@/components/admin/trading-analysis/DuplicateTimestampChecker';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PatternAnalysisConfig extends AnalysisConfig {
  analysisMode: AnalysisMode;
}

const TradingPatternAnalysisPage = () => {
  const isMobile = useIsMobile();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tradingFiles, setTradingFiles] = useState<TradingFile[]>([]);
  const [patterns, setPatterns] = useState<TradingPattern[]>([]);
  const [selectedFileForView, setSelectedFileForView] = useState<TradingFile | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingPatterns, setIsLoadingPatterns] = useState(false);
  const [predictionResult, setPredictionResult] = useState<PredictionResult | null>(null);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisConfig, setAnalysisConfig] = useState<PatternAnalysisConfig>({
    ...DEFAULT_CONFIGS.basic,
    analysisMode: 'basic' as AnalysisMode
  });
  const [predictionSequence, setPredictionSequence] = useState('');
  const [activeTab, setActiveTab] = useState('realtime-predictions');
  const [availablePatternModes, setAvailablePatternModes] = useState<{[fileId: string]: AnalysisMode[]}>({});
  const [isPredicting, setIsPredicting] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'patterns'>('list');
  const [selectedAnalysisMode, setSelectedAnalysisMode] = useState<AnalysisMode>('basic');
  const [hasPatternsForMode, setHasPatternsForMode] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  

  // Load trading files on mount
  useEffect(() => {
    loadTradingFiles();
  }, []);


  const loadTradingFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const result = await TradingPatternAnalysisService.getTradingFiles();
      if (result.success && result.data) {
        setTradingFiles(result.data);
      } else {
        toast.error(result.error || 'Failed to load trading files');
      }
    } catch (error) {
      console.error('Error loading trading files:', error);
      toast.error('Failed to load trading files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadPatterns = async (fileId: string) => {
    setIsLoadingPatterns(true);
    try {
      const result = await TradingPatternAnalysisService.getPatterns(fileId);
      if (result.success && result.data) {
        setPatterns(result.data);
        setSelectedFileForView(tradingFiles.find(f => f.id === fileId) || null);
      } else {
        toast.error(result.error || 'Failed to load patterns');
      }
    } catch (error) {
      console.error('Error loading patterns:', error);
      toast.error('Failed to load patterns');
    } finally {
      setIsLoadingPatterns(false);
    }
  };

  const handleViewPatterns = async (file: TradingFile) => {
    if (!file?.id) {
      toast.error('Invalid file selected');
      return;
    }

    try {
      setSelectedFileForView(file);
      setIsLoadingPatterns(true);
      
      console.log('🔍 Loading patterns for file:', file.filename, 'Mode:', selectedAnalysisMode);
      
      // Get available analysis modes for this file
      const availableModes = await TradingAnalysisOrchestrator.getAvailableAnalysisModes(file.id);
      console.log('📊 Available modes:', availableModes);
      
      if (availableModes.length === 0) {
        console.log('⚠️ No analysis modes available for file');
        setHasPatternsForMode(false);
        setAnalysisResults(null);
        setViewMode('patterns');
        setActiveTab('patterns');
        return;
      }

      // Use the selected mode if available, otherwise use the first available mode
      const modeToUse = availableModes.includes(selectedAnalysisMode) ? selectedAnalysisMode : availableModes[0];
      console.log('🎯 Using analysis mode:', modeToUse);
      
      // Load comprehensive analysis results
      const analysisResult = await TradingAnalysisOrchestrator.getAnalysisResults(file.id, modeToUse);
      console.log('📈 Analysis result:', analysisResult);
      
      if (analysisResult) {
        setAnalysisResults(analysisResult);
        setHasPatternsForMode(true);
        setSelectedAnalysisMode(modeToUse);
        console.log('✅ Analysis results loaded successfully');
      } else {
        console.log('❌ No analysis results found');
        setHasPatternsForMode(false);
        setAnalysisResults(null);
      }
      
      setViewMode('patterns');
      setActiveTab('patterns');
    } catch (error) {
      console.error('❌ Error viewing patterns:', error);
      toast.error('Failed to load patterns: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsLoadingPatterns(false);
    }
  };

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const trades = results.data as CSVTradeData[];
            
            if (trades.length === 0) {
              toast.error('CSV file is empty or invalid');
              return;
            }

            const tradingPatternService = new TradingPatternAnalysisService();
            const binarySequence = tradingPatternService.convertToBinarySequence(trades);
            
            const winTrades = binarySequence.filter(x => x === 1).length;
            const lossTrades = binarySequence.filter(x => x === 0).length;

            const fileData = {
              filename: file.name,
              file_size: file.size,
              total_trades: trades.length,
              win_trades: winTrades,
              loss_trades: lossTrades
            };

            const saveResult = await tradingPatternService.saveTradingFile(fileData);
            
            if (saveResult.success && saveResult.data) {
              await supabase
                .from('trading_files')
                .update({ trading_data: binarySequence })
                .eq('id', saveResult.data.id);

              toast.success('File uploaded successfully');
              await loadTradingFiles();
              setActiveTab('analysis');
            } else {
              toast.error(saveResult.error || 'Failed to save file');
            }
          } catch (error) {
            console.error('Error processing file:', error);
            toast.error('Failed to process file');
          } finally {
            setIsUploading(false);
            setUploadProgress(0);
          }
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          toast.error('Failed to parse CSV file');
          setIsUploading(false);
          setUploadProgress(0);
        }
      });
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, []);

  const handleRunAnalysis = async (file: TradingFile) => {
    if (!file?.id) {
      toast.error('Please select a valid file');
      return;
    }

    setIsAnalyzing(true);
    try {
      // Import the service
      const { ReAnalysisService } = await import('@/services/ReAnalysisService');
      
      // 1. Clear pattern backups to avoid conflicts
      console.log('🧹 Clearing pattern backups for file:', file.id);
      const backupResult = await ReAnalysisService.clearPatternBackups(file.id);
      if (backupResult.success && backupResult.deletedCount! > 0) {
        console.log(`✅ Cleared ${backupResult.deletedCount} pattern backups`);
      }

      // 2. Reset re-analysis status for all linked bots
      console.log('🔄 Resetting re-analysis status for linked bots');
      const resetResult = await ReAnalysisService.resetReAnalysisStatus(file.id);
      if (resetResult.success && resetResult.affectedCount! > 0) {
        console.log(`✅ Reset re-analysis status for ${resetResult.affectedCount} bots`);
      }

      // 3. Run pattern analysis
      console.log('🧠 Running pattern analysis...');
      const result = await TradingAnalysisOrchestrator.analyzePatterns(
        file.id,
        analysisConfig.analysisMode,
        analysisConfig
      );

      if (result) {
        setAnalysisResults(result);
        // Trigger refresh for real-time components
        setRefreshTrigger(prev => prev + 1);
        toast.success('Pattern analysis completed - Re-analysis unlocked for linked bots');
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error(error instanceof Error ? error.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleManualPrediction = async () => {
    if (!selectedFileForView?.id || !predictionSequence.trim()) {
      toast.error('Please select a file and enter a sequence');
      return;
    }

    const sequence = predictionSequence.trim();
    if (!/^[01,\s]+$/.test(sequence)) {
      toast.error('Sequence must contain only 0s, 1s, spaces, and commas');
      return;
    }

    setIsPredicting(true);
    try {
      const result = await TradingAnalysisOrchestrator.predictNextTrade(
        selectedFileForView.id,
        sequence,
        analysisConfig.analysisMode,
        analysisConfig
      );

      setPredictionResult(result);
      toast.success('Prediction completed');
    } catch (error) {
      console.error('Prediction failed:', error);
      toast.error(error instanceof Error ? error.message : 'Prediction failed');
    } finally {
      setIsPredicting(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this file and all its patterns?')) {
      return;
    }

    try {
      const result = await TradingPatternAnalysisService.deleteTradingFile(fileId);
      
      if (result.success) {
        toast.success('File deleted successfully');
        await loadTradingFiles();
        if (selectedFileForView?.id === fileId) {
          setSelectedFileForView(null);
          setAnalysisResults(null);
        }
      } else {
        toast.error(result.error || 'Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };


  return (
    <div className={`${isMobile ? 'px-4 py-4' : 'p-6'} space-y-6`}>
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold`}>Trading Pattern Analysis</h1>
          <p className={`text-muted-foreground ${isMobile ? 'text-sm' : ''}`}>
            Advanced pattern recognition and prediction system
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 p-1 w-full h-auto">
          <TabsTrigger value="upload" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Upload className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Upload' : 'Upload Data'}</span>
          </TabsTrigger>
          <TabsTrigger value="analysis" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Brain className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Analysis' : 'Pattern Analysis'}</span>
          </TabsTrigger>
          <TabsTrigger value="patterns" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Target className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Results' : 'View Results'}</span>
          </TabsTrigger>
          <TabsTrigger value="prediction" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <TrendingUp className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Predict' : 'Manual Prediction'}</span>
          </TabsTrigger>
          <TabsTrigger value="progressive-backtest" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <BarChart3 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Backtest' : 'Progressive Backtest'}</span>
          </TabsTrigger>
          <TabsTrigger value="honest-backtest" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Shield className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Honest' : 'Honest Backtest'}</span>
          </TabsTrigger>
          <TabsTrigger value="optimization" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <TrendingUp className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Optimize' : 'Auto Optimization'}</span>
          </TabsTrigger>
          <TabsTrigger value="realtime-predictions" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Target className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Real-time' : 'Real-time Predictions'}</span>
          </TabsTrigger>
          <TabsTrigger value="pattern-comparison" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <GitCompare className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Compare' : 'Pattern Comparison'}</span>
          </TabsTrigger>
          <TabsTrigger value="sequence-comparison" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Activity className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Sequence' : 'Sequence Comparison'}</span>
          </TabsTrigger>
          <TabsTrigger value="trade-editor" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Settings className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Editor' : 'Trade Editor'}</span>
          </TabsTrigger>
          <TabsTrigger value="data-qa" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : 'flex items-center'}`}>
            <Activity className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Data QA' : 'Realtime Data QA'}</span>
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Upload Trading Data
              </CardTitle>
              <CardDescription>
                Upload CSV files containing your trading history for pattern analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`border-2 border-dashed border-muted-foreground/25 rounded-lg ${isMobile ? 'p-4' : 'p-8'} text-center`}>
                <Upload className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} mx-auto mb-4 text-muted-foreground`} />
                <div className="space-y-2">
                  <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium`}>Drop your CSV file here or click to browse</h3>
                  <p className="text-sm text-muted-foreground">
                    Supported format: CSV files with trade data including P&L information
                  </p>
                </div>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="mt-4"
                >
                  {isUploading ? 'Uploading...' : 'Select CSV File'}
                </Button>
              </div>

              {isUploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trading Files List */}
          <Card>
            <CardHeader>
              <CardTitle>Uploaded Files</CardTitle>
              <CardDescription>
                Manage your uploaded trading data files
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingFiles ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-sm text-muted-foreground">Loading files...</p>
                </div>
              ) : tradingFiles.length === 0 ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-lg font-medium mb-2">No files uploaded yet</p>
                  <p className="text-sm text-muted-foreground">
                    Upload your first CSV file to get started with pattern analysis
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {isMobile ? (
                    // Mobile: Card-based layout
                    <div className="space-y-3">
                      {tradingFiles.map((file) => (
                        <Card key={file.id} className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm truncate">{file.filename}</h4>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewPatterns(file)}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteFile(file.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Trades:</span>
                                <span>{file.total_trades}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Win Rate:</span>
                                <Badge variant="outline" className="text-xs">
                                  {((file.win_trades / file.total_trades) * 100).toFixed(1)}%
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Status:</span>
                                <Badge variant={file.status === 'processed' ? 'default' : 'secondary'} className="text-xs">
                                  {file.status}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Uploaded:</span>
                                <span>{new Date(file.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    // Desktop: Table layout
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Filename</TableHead>
                            <TableHead>Trades</TableHead>
                            <TableHead>Win Rate</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Uploaded</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tradingFiles.map((file) => (
                            <TableRow key={file.id}>
                              <TableCell className="font-medium">{file.filename}</TableCell>
                              <TableCell>{file.total_trades}</TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {((file.win_trades / file.total_trades) * 100).toFixed(1)}%
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={file.status === 'processed' ? 'default' : 'secondary'}>
                                  {file.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {new Date(file.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewPatterns(file)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteFile(file.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-6">
          <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'lg:grid-cols-3 gap-6'}`}>
            {/* Analysis Configuration */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Analysis Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="analysis-mode">Analysis Mode</Label>
                  <Select
                    value={analysisConfig.analysisMode}
                    onValueChange={(value: AnalysisMode) => {
                      setAnalysisConfig(prev => ({
                        ...DEFAULT_CONFIGS[value],
                        analysisMode: value
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select analysis mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic Analysis</SelectItem>
                      <SelectItem value="intermediate">Intermediate Analysis</SelectItem>
                      <SelectItem value="advanced">Advanced Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min-pattern-length">Min Pattern Length</Label>
                    <Input
                      id="min-pattern-length"
                      type="number"
                      min="2"
                      max="10"
                      value={analysisConfig.minPatternLength}
                      onChange={(e) => setAnalysisConfig(prev => ({
                        ...prev,
                        minPatternLength: parseInt(e.target.value) || 2
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-pattern-length">Max Pattern Length</Label>
                    <Input
                      id="max-pattern-length"
                      type="number"
                      min="3"
                      max="15"
                      value={analysisConfig.maxPatternLength}
                      onChange={(e) => setAnalysisConfig(prev => ({
                        ...prev,
                        maxPatternLength: parseInt(e.target.value) || 8
                      }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="min-frequency">Minimum Frequency</Label>
                  <Input
                    id="min-frequency"
                    type="number"
                    min="1"
                    max="10"
                    value={analysisConfig.minFrequency}
                    onChange={(e) => setAnalysisConfig(prev => ({
                      ...prev,
                      minFrequency: parseInt(e.target.value) || 2
                    }))}
                  />
                </div>
              </CardContent>
            </Card>

            {/* File Selection & Analysis */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  Run Pattern Analysis
                </CardTitle>
                <CardDescription>
                  Analyze uploaded files to detect trading patterns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {tradingFiles.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-lg font-medium mb-2">No files available</p>
                    <p className="text-sm text-muted-foreground">
                      Upload CSV files first to run pattern analysis
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                     {tradingFiles.map((file) => (
                       <div key={file.id} className="border rounded-lg p-4 space-y-3">
                         <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'}`}>
                           <div className={isMobile ? 'text-center' : ''}>
                             <h4 className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{file.filename}</h4>
                             <p className={`text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>
                               {file.total_trades} trades • {((file.win_trades / file.total_trades) * 100).toFixed(1)}% win rate
                             </p>
                           </div>
                           <Button
                             onClick={() => handleRunAnalysis(file)}
                             disabled={isAnalyzing}
                             size={isMobile ? 'sm' : 'default'}
                             className={isMobile ? 'w-full' : ''}
                           >
                             {isAnalyzing ? 'Analyzing...' : 'Run Analysis'}
                           </Button>
                         </div>
                       </div>
                     ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pattern Analysis Tab */}
        <TabsContent value="patterns" className="space-y-6">
          {viewMode === 'list' ? (
            <Card>
              <CardHeader>
                <CardTitle>Available Files for Pattern View</CardTitle>
                <CardDescription>
                  Select a file to view its pattern analysis results
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tradingFiles.length === 0 ? (
                  <div className="text-center py-8">
                    <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No files available</p>
                    <p className="text-sm text-muted-foreground">
                      Upload and analyze files first to view patterns
                    </p>
                  </div>
                ) : (
                   <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}`}>
                     {tradingFiles.map((file) => (
                       <Card key={file.id} className="border border-border/50 hover:border-border cursor-pointer" onClick={() => handleViewPatterns(file)}>
                         <CardHeader className={isMobile ? 'pb-2' : 'pb-3'}>
                           <CardTitle className={`${isMobile ? 'text-sm' : 'text-base'}`}>{file.filename}</CardTitle>
                           <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-2'}`}>
                             <Badge variant="outline" className={isMobile ? 'text-xs' : ''}>{file.total_trades} trades</Badge>
                             <Badge variant="outline" className={isMobile ? 'text-xs' : ''}>{((file.win_trades / file.total_trades) * 100).toFixed(1)}%</Badge>
                           </div>
                         </CardHeader>
                         <CardContent className={isMobile ? 'pt-0' : ''}>
                           <Button className="w-full" size="sm">
                             <Eye className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-2`} />
                             {isMobile ? 'View' : 'View Patterns'}
                           </Button>
                         </CardContent>
                       </Card>
                     ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Pattern Analysis Results</h2>
                  {selectedFileForView && (
                    <p className="text-muted-foreground">
                      Analyzing: {selectedFileForView.filename}
                    </p>
                  )}
                </div>
                <Button variant="outline" onClick={() => setViewMode('list')}>
                  ← Back to Files
                </Button>
              </div>

              {/* Pattern Analysis Results */}
              {analysisResults ? (
                <div className="space-y-4">
                  {/* Analysis Mode Selector */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Analysis Configuration
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        <Label htmlFor="analysis-mode">Analysis Mode:</Label>
                        <Select
                          value={selectedAnalysisMode}
                          onValueChange={(value: 'basic' | 'intermediate' | 'advanced') => {
                            setSelectedAnalysisMode(value);
                            // Reload analysis for the new mode
                            if (selectedFileForView) {
                              handleViewPatterns(selectedFileForView);
                            }
                          }}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Select analysis mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic Analysis</SelectItem>
                            <SelectItem value="intermediate">Intermediate Analysis</SelectItem>
                            <SelectItem value="advanced">Advanced Analysis</SelectItem>
                          </SelectContent>
                        </Select>
                        <Badge variant="outline" className="ml-2">
                          {analysisResults.totalPatterns} patterns
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <ComprehensiveAnalysisResults result={analysisResults} />
                  {/* Debug info */}
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg text-xs">
                    <p>Debug: {JSON.stringify({
                      hasResult: !!analysisResults,
                      totalPatterns: analysisResults?.totalPatterns,
                      patternsCount: analysisResults?.patterns?.length,
                      mode: analysisResults?.mode
                    }, null, 2)}</p>
                  </div>
                </div>
              ) : (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-center text-muted-foreground">
                      <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No analysis results available</p>
                      <p className="text-sm">
                        {selectedFileForView 
                          ? `Run pattern analysis on "${selectedFileForView.filename}" to see comprehensive results here.`
                          : "Select a file and run pattern analysis to see results here."
                        }
                      </p>
                      {selectedFileForView && !hasPatternsForMode && (
                        <div className="mt-4 p-4 bg-muted rounded-lg">
                          <p className="text-sm">
                            No patterns found for <Badge variant="outline">{selectedAnalysisMode}</Badge> mode.
                            Try running pattern analysis first.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>


        {/* Manual Prediction Tab */}
        <TabsContent value="prediction" className="space-y-6">
          <ManualPredictionComparison
            tradingFiles={tradingFiles}
            selectedFile={selectedFileForView}
            onFileSelect={(file) => setSelectedFileForView(file)}
            analysisConfig={analysisConfig}
            hasPatternsForMode={hasPatternsForMode}
          />
        </TabsContent>

        {/* Progressive Backtest Tab */}
        <TabsContent value="progressive-backtest" className="space-y-6">
          <ProgressiveBacktestTab 
            selectedFile={selectedFileForView}
            selectedAnalysisMode={selectedAnalysisMode}
          />
        </TabsContent>

        {/* Honest Progressive Backtest Tab */}
        <TabsContent value="honest-backtest" className="space-y-6">
          <HonestProgressiveBacktestTab 
            selectedFile={selectedFileForView}
          />
        </TabsContent>

        {/* Auto Optimization Tab */}
        <TabsContent value="optimization" className="space-y-6">
          <OptimizationTab 
            selectedFile={selectedFileForView}
            selectedAnalysisMode={selectedAnalysisMode}
            analysisConfig={analysisConfig}
          />
        </TabsContent>

        {/* Real-time Predictions Tab */}
        <TabsContent value="realtime-predictions" className="space-y-6">
          <RealTimePredictionsTab refreshTrigger={refreshTrigger} />
        </TabsContent>

        {/* Pattern Comparison Tab */}
        <TabsContent value="pattern-comparison" className="space-y-6">
          <CrossPatternComparisonTab />
        </TabsContent>

        {/* Sequence Comparison Tab */}
        <TabsContent value="sequence-comparison" className="space-y-6">
          <SequenceComparisonTab />
        </TabsContent>

        {/* Trade Value Editor Tab */}
        <TabsContent value="trade-editor" className="space-y-6">
          <TradeValueEditor onRefresh={loadTradingFiles} />
        </TabsContent>

        {/* Realtime Data QA Tab */}
        <TabsContent value="data-qa" className="space-y-6">
          <DuplicateTimestampChecker />
        </TabsContent>

        {/* Pattern Management Tab - Removed and backed up as .OLD files */}
      </Tabs>
    </div>
  );
};

export default TradingPatternAnalysisPage;